<template>
  <div class="card status-card">
    <p class="eyebrow">Workshop</p>
    <h1>{{ title }}</h1>
    <p class="lead">{{ body }}</p>
    <p v-if="collegeName" class="college muted">
      {{ collegeName }}<template v-if="me?.workshopDate"> · {{ formatWhen(me.workshopDate) }}</template>
    </p>
    <div class="actions">
      <button
        v-if="canEnter"
        class="btn btn-primary"
        @click="router.push('/slides/01')"
      >
        Enter the workshop
      </button>
      <button class="btn btn-secondary" :disabled="signingOut" @click="logout">
        {{ signingOut ? 'Signing out…' : 'Sign out' }}
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { getMe } from '../services/api'
import { signOutAndLeave } from '../services/auth'

const JUST_SELECTED_KEY = 'u80_just_selected_college'

const router = useRouter()
const me = ref(null)
const signingOut = ref(false)
const justSelected = ref(false)

function formatWhen(iso) {
  if (!iso) return ''
  try {
    return new Date(`${iso}T12:00:00`).toLocaleString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

const collegeName = computed(() => me.value?.collegeName || '')
const state = computed(() => {
  const raw = me.value?.workshopState || null
  return raw === 'requested' ? null : raw
})
const canEnter = computed(
  () =>
    state.value === 'today' &&
    me.value?.status === 'approved' &&
    !!me.value?.collegeId &&
    me.value?.sessionEnabled !== false,
)

const title = computed(() => {
  if (state.value === 'unscheduled') return 'Thanks for the interest'
  if (state.value === 'today') return 'Thanks for your interest'
  if (state.value === 'upcoming') return 'Thanks for your interest'
  if (state.value === 'past') {
    return justSelected.value ? 'The workshop was done' : 'Thanks for your interest'
  }
  return 'Thanks for your interest'
})

const body = computed(() => {
  const when = formatWhen(me.value?.workshopDate)
  if (state.value === 'unscheduled') {
    return 'Thanks for the interest. We will schedule a workshop soon and let you know.'
  }
  if (state.value === 'today') {
    if (canEnter.value) return 'The workshop is open.'
    if (me.value?.status === 'approved') {
      return 'The workshop is happening now. Refresh, or ask a facilitator if you still cannot enter.'
    }
    return "The workshop is happening now. You'll be able to enter after you're approved."
  }
  if (state.value === 'upcoming') {
    return when
      ? `Thanks for your interest — you will be able to go to the workshop on ${when}.`
      : 'Thanks for your interest — you will be able to go to the workshop on the scheduled date.'
  }
  if (state.value === 'past') {
    if (justSelected.value) {
      return 'The workshop was done. We will be sending the materials soon to you.'
    }
    return 'Thanks for your interest — We will be sending the materials soon to you.'
  }
  return 'Thanks for your interest.'
})

async function logout() {
  if (signingOut.value) return
  signingOut.value = true
  try {
    await signOutAndLeave()
  } catch {
    signingOut.value = false
  }
}

onMounted(async () => {
  try {
    justSelected.value = sessionStorage.getItem(JUST_SELECTED_KEY) === '1'
    sessionStorage.removeItem(JUST_SELECTED_KEY)
  } catch {
    justSelected.value = false
  }
  try {
    me.value = await getMe()
  } catch {
    me.value = null
  }
  const leftoverRequest =
    me.value?.workshopState === 'requested' ||
    (me.value?.requestedCollegeName && !me.value?.collegeId)
  if (leftoverRequest || (!me.value?.collegeId && !me.value?.collegeName)) {
    router.replace('/select-college')
  }
})
</script>

<style scoped>
.status-card {
  max-width: 32rem;
  margin: 0 auto;
  text-align: left;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--teal);
  margin: 0 0 0.5rem;
}

h1 {
  margin: 0 0 0.75rem;
  font-size: 1.75rem;
}

.lead {
  margin: 0 0 0.75rem;
  font-size: 1.05rem;
}

.college {
  margin: 0 0 1.5rem;
  font-weight: 600;
}

.actions {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
</style>
